// src/salesAssessment/SalesAssessmentComparisonSetup.js
import React from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Search as SearchIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const TestCard = styled(Card)(({ theme, selected }) => ({
  marginBottom: theme.spacing(2),
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  backgroundColor: selected ? theme.palette.primary.light + '10' : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '20' : '#f5f5f5',
  },
}));

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function SalesAssessmentComparisonSetup({
  positions,
  targetPosition,
  salesTests,
  selectedTests,
  loading,
  onTargetPositionChange,
  onTestSelection,
  onStartComparison,
  canStart,
}) {
  const { t } = useTranslation();
  const [testFilter, setTestFilter] = React.useState('');

  const selectedCount = Object.keys(selectedTests).filter(id => selectedTests[id]).length;

  const filteredTests = salesTests.filter(test => {
    if (!testFilter) return true;
    const q = testFilter.toLowerCase();
    return (
      test.applicantName?.toLowerCase().includes(q) ||
      test.applicantEmail?.toLowerCase().includes(q)
    );
  });

  // ── Panel izquierdo: selector de cargo ──────────────────────────────────
  const renderPositionSelector = () => (
    <StyledPaper sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        🎯 {t('salesAssessment.comparison.selectPosition')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('salesAssessment.comparison.selectPositionDescription')}
      </Typography>

      <Autocomplete
        value={targetPosition}
        onChange={(event, newValue) => onTargetPositionChange(newValue)}
        options={positions}
        getOptionLabel={(option) => option.name || ''}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {option.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {option.industria}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('salesAssessment.comparison.positionPlaceholder')}
            placeholder={t('salesAssessment.comparison.positionPlaceholder')}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading.positions && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loading={loading.positions}
        noOptionsText={t('salesAssessment.comparison.noPositionsFound')}
      />

      {targetPosition && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            {t('salesAssessment.comparison.selectedPosition')}:
          </Typography>
          <Typography variant="body2">
            <strong>{targetPosition.name}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {targetPosition.industria}
          </Typography>
        </Box>
      )}
    </StyledPaper>
  );

  // ── Panel central: lista de tests ───────────────────────────────────────
  const renderTestsList = () => {
    if (loading.tests) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('salesAssessment.comparison.loadingTests')}
          </Typography>
        </Box>
      );
    }

    if (filteredTests.length === 0) {
      return (
        <Alert severity="info">
          {testFilter
            ? t('noResults')
            : t('salesAssessment.comparison.noEvaluatedTests')}
        </Alert>
      );
    }

    return (
      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {filteredTests.map((test) => {
          const isSelected = !!selectedTests[test.id];

          return (
            <TestCard
              key={test.id}
              selected={isSelected}
              onClick={(e) => {
                if (
                  e.target.tagName !== 'INPUT' &&
                  !e.target.closest('button') &&
                  !e.target.closest('.MuiCheckbox-root')
                ) {
                  onTestSelection(test.id);
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onTestSelection(test.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    color="primary"
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {test.applicantName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {test.applicantEmail}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {t('salesAssessment.comparison.evaluatedAt')}: {formatDate(test.processedAt || test.completedAt)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </TestCard>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>

        {/* ── Columna izquierda: cargo + resumen ── */}
        <Grid item xs={12} md={4}>
          {renderPositionSelector()}

          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              📊 {t('summary')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('salesAssessment.comparison.position')}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {targetPosition ? targetPosition.name : t('salesAssessment.comparison.noPosition')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('salesAssessment.comparison.selectedPersons')}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedCount} {t('salesAssessment.comparison.persons')}
              </Typography>
            </Box>

            {selectedCount > 0 && (
              <Box sx={{ mt: 2 }}>
                {Object.keys(selectedTests)
                  .filter(id => selectedTests[id])
                  .map(id => {
                    const test = salesTests.find(t => t.id === id);
                    return test ? (
                      <Chip
                        key={id}
                        label={test.applicantName}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ) : null;
                  })}
              </Box>
            )}
          </StyledPaper>
        </Grid>

        {/* ── Columna central: lista de postulantes ── */}
        <Grid item xs={12} md={5}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                👤 {t('salesAssessment.comparison.evaluatedTests')} ({filteredTests.length})
              </Typography>
              {loading.tests && <CircularProgress size={20} />}
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder={t('salesAssessment.comparison.searchApplicant')}
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {t('salesAssessment.comparison.onlyEvaluatedNotice')}
              </Typography>
            </Alert>

            {renderTestsList()}
          </StyledPaper>
        </Grid>

        {/* ── Columna derecha: requisitos + botón iniciar ── */}
        <Grid item xs={12} md={3}>
          <StyledPaper sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              ✅ {t('salesAssessment.comparison.requirements')}
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary={t('salesAssessment.comparison.requirementMinTests')}
                  secondary={selectedCount >= 2 ? '✓' : `✗ (${selectedCount}/2)`}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: selectedCount >= 2 ? 'text.primary' : 'text.secondary',
                  }}
                  secondaryTypographyProps={{
                    color: selectedCount >= 2 ? 'success.main' : 'error.main',
                    fontWeight: 'bold',
                  }}
                />
              </ListItem>
            </List>
          </StyledPaper>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={
              loading.creating ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <StartIcon />
              )
            }
            onClick={onStartComparison}
            disabled={!canStart || loading.creating}
          >
            {loading.creating
              ? t('salesAssessment.comparison.creating')
              : t('salesAssessment.comparison.startComparison')}
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
}